// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Divider, Paper, Button, CircularProgress } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PDFDownloadLink } from '@react-pdf/renderer';
import downloadIcon from 'Assets/download-dark-icon.png';
import { useAppDispatch, useAppSelector } from 'Hooks';
import OrderDialog from 'Features/order';
import { RootState } from 'Store';
import { GetOrdersThunk, GetPdfDataThunk } from 'Features/order/orderThunk';
import CustomOrderDialog from 'Features/order/CustomOrderDialog';
import PDF from 'Features/order/PDF';
import Sidebar from 'Components/Sidebar';
import UploadFilesDialog from 'Components/UploadFilesDialog';
import { getOrders } from 'Features/order/orderSelectors';
import { HideUploadSingleFileDialogAction } from 'Features/fileUpload/fileUploadActions';
import { SetShowSidebarAction } from 'Features/map/mapEditorActions';
import { getRasterRequestStatus, selectCRSInfo } from '../projectSelectors';
import DataSetsPanel from './DataSetsPanel';
import KmlPanel from './KmlPanel';
import ProjectHeader from './ProjectHeader';
import OrderSection from './OrderSection';
import LayersPanel from './LayersPanel';
import stylesDef from './styles';

const ProjectSidebar = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const { project } = useAppSelector((state) => state.project);
  const { screenshot } = useAppSelector((state) => state.map.common);
  const orders = useAppSelector((state: RootState) => getOrders(state));
  const { activeOrder: order, projectOrdersReverse } = orders;

  const ordersWithKml = useMemo(
    () => projectOrdersReverse.filter((o: IOrder) => !!o.boundaryFile),
    [projectOrdersReverse],
  );

  const [selectedOrderId, setSelectedOrderId] = useState<string>('');
  const prevOrderIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (ordersWithKml.length > 0) {
      const currentIds = new Set(ordersWithKml.map((o) => o._id));
      const activeJustGotKml = order && currentIds.has(order._id) && !prevOrderIdsRef.current.has(order._id);

      if (activeJustGotKml) {
        setSelectedOrderId(order._id);
      } else {
        setSelectedOrderId((prev) => {
          if (prev && currentIds.has(prev)) return prev;
          return ordersWithKml[ordersWithKml.length - 1]._id;
        });
      }

      prevOrderIdsRef.current = currentIds;
    }
  }, [ordersWithKml]);

  const rasterRequest = useSelector((state: RootState) => getRasterRequestStatus(state));

  const { name: projectName, imageryRequest, _id: projectId } = project || {};
  const { rasterizationCreditCost } = order || {};
  const imageryRequested = (order?.isEstimate && !!(project?.imageryRequest?.requested)) || !!(project?.imageryRequest?.uploaded) || !!(project?.imageryRequest?.notificationSent);

  const showSidebar = useAppSelector((state) => state.map.editor.present.showSidebar);

  const [orderFormVisible, setOrderFormVisible] = useState(false);
  const [customOrderFormVisible, setCustomOrderFormVisible] = useState(false);
  const [uploadFilesFormVisible, setUploadFilesFormVisible] = useState(false);
  const [showLayersPanel, setShowLayersPanel] = useState(false);
  const needUploadFiles = (!order?.boundaryFile);
  const { customCheckoutOptionalRasterization, amtChild } = useFlags();

  const [pdfData, setPdfData] = useState<any>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    if ((imageryRequest || !!rasterizationCreditCost) && order.isEstimate) {
      const loadData = async () => {
        await dispatch(GetOrdersThunk(projectId));
      };

      loadData();
    }
  }, [order?.isEstimate, order?.costToCustomer]);

  const onButtonClick = () => {
    if (customCheckoutOptionalRasterization && customCheckoutOptionalRasterization.raster !== undefined) {
      setCustomOrderFormVisible(true);
    } else {
      setOrderFormVisible(true);
    }
  };

  useEffect(() => {
    if (order && order.isEstimate) {
      setPdfLoading(true);
      const fetchPdfData = async () => {
        const result = await dispatch(GetPdfDataThunk(order._id));
        if (result.success) {
          const pdfData = {
            ...result.data.pdfData,
            screenshot,
          }
          setPdfData(pdfData);
        }
        setPdfLoading(false);
      }

      fetchPdfData();
    }

    return () => {
      setPdfData(null);
    };
  }, [order]);

  if (!showSidebar) {
    return (
      <Sidebar
        collapsed
        onExpand={() => dispatch(SetShowSidebarAction(true))}
        onClick={() => dispatch(HideUploadSingleFileDialogAction())}
      />
    );
  }

  // When LayersPanel is active, replace the entire sidebar content
  if (showLayersPanel) {
    return (
      <Sidebar onClick={() => dispatch(HideUploadSingleFileDialogAction())}>
        <Box sx={styles.sidebarContent}>
          <LayersPanel orderId={selectedOrderId} onBack={() => setShowLayersPanel(false)} />
        </Box>
      </Sidebar>
    );
  }

  return (
    <Sidebar onClick={() => dispatch(HideUploadSingleFileDialogAction())}>
      <Box sx={styles.sidebarContent}>
        <ProjectHeader onToggleCollapse={() => dispatch(SetShowSidebarAction(false))} />
        <Divider />
        <KmlPanel />
        <Divider />
        <DataSetsPanel />
        <Divider />
        {ordersWithKml.length > 0 && (
          <OrderSection
            orders={ordersWithKml}
            selectedOrderId={selectedOrderId}
            onSelectOrder={setSelectedOrderId}
            activeOrderId={order?._id}
            onViewLayers={() => setShowLayersPanel(true)}
          />
        )}
      </Box>
      {order?.isEstimate && order?.bundleId && (imageryRequested || rasterRequest.requested) && screenshot ? (
        <Box sx={pdfData ? styles.downloadContainer : styles.loadingDownloadContainer}>
          {pdfLoading && <CircularProgress size={24}/>}
          {!pdfLoading && pdfData && (
            <PDFDownloadLink document={<PDF data={pdfData} />} fileName={`${projectName}_estimate.pdf`} style={styles.pdfButton}>
              {({ loading }) =>
                (loading ? <CircularProgress size={24} /> : (
                  <Box
                    sx={styles.orderButton}
                  >
                    <img style={styles.downloadImage} src={downloadIcon} alt="download" />
                    DOWNLOAD ESTIMATE
                  </Box>
                ))}
            </PDFDownloadLink>
          )}
        </Box>
      )
        : (
          <Paper sx={styles.orderButtonWrapper} elevation={2}>
            <span>
              <Button
                variant="outlined"
                fullWidth
                sx={styles.orderButton}
                onClick={onButtonClick}
                disabled={needUploadFiles || order.acreage <= 0 || imageryRequested || rasterRequest.rejected}
              >
                Go to Checkout
              </Button>
            </span>
          </Paper>
        )}
      <OrderDialog orderFormVisible={orderFormVisible} setOrderFormVisible={setOrderFormVisible} />
      <CustomOrderDialog orderFormVisible={customOrderFormVisible} setOrderFormVisible={setCustomOrderFormVisible} />
      <UploadFilesDialog showDialog={uploadFilesFormVisible} setShowDialog={setUploadFilesFormVisible} />
    </Sidebar>
  );
};

export default ProjectSidebar;
